import React, { Fragment } from 'react';
import axios from 'axios';
import Linkify from 'react-linkify';
import Moment from 'react-moment';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSnackbar } from 'notistack';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Fab from '@material-ui/core/Fab';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import OpenInNew from '@material-ui/icons/OpenInNew';
import CircularProgress from '@material-ui/core/CircularProgress';
import HomeworkGroupHeaderBadge from './HomeworkGroupHeaderBadge';
import { setData } from '../../../actions/index';
import ConfirmDialog from '../ConfirmDialog';
import { getHomeworkStatus } from '../../../helpers';
import FileAttachmentRow from "../FileAttachmentRow";
import FileAttachmentDownloadDialog from "../../shared/FileAttachmentDownloadDialog";

const styles = theme => ({
  paddingVertical: {
    padding: '20px 0',
  },
  breakWord: {
    wordWrap: 'break-word',
  },
  contentHeading: {
    fontSize: 22,
    marginBottom: 10,
    wordWrap: 'break-word',
  },
  contentSubHeading: {
    fontSize: 14,
    color: '#797979',
    textTransform: 'uppercase',
    wordWrap: 'break-word',
  },
  contentMeta: {
    lineHeight: 1.6,
    padding: '20px 0',
    wordWrap: 'break-word',
  },
  contentTitle: {
    fontSize: 18,
    wordWrap: 'break-word',
  },
  contentSection: {
    flexWrap: 'nowrap',
    marginTop: 10,
    paddingTop: 20,
    borderTop: '1px solid #eee',
  },
  dialogHomeworkCompletedWrapper: {
    backgroundColor: '#f5f5f5',
    padding: '10px 20px',
  },
  homeworkLinkListItemText: {
    color: '#2096f3',
  },
  link: {
    color: '#f56a12',
    cursor: 'pointer',
  },
  buttonIcon: {
    marginBottom: -7,
  },
  closeButtonIcon: {
    float: 'right',
    marginBottom: -7,
  },
  uploadAttachmentIcon: {
    marginRight: 10,
  },
  uploadAttachmentLoader: {
    marginLeft: 10,
  },
});

const mapStateToProps = state => {
  return {
    user: state.user,
    selectedPupil: state.selectedPupil,
    defaultDateRangeHomework: state.defaultDateRangeHomework,
  };
};

function mapDispatchToProps(dispatch) {
  return {
    setData: (data, type) => dispatch(setData(data, type)),
  };
}

class HomeworkDetails extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isHomeworkAttachmentUploading: false,
      isConfirmationDialogOpen: false,
      confirmationDialogHomeworkAttachmentId: 0,
    };

    this.maxFileSize = 262144000;
    this.homeworkAttachmentInput = React.createRef();

    this.onHomeworkCompletedChange = this.onHomeworkCompletedChange.bind(this);
    this.onHomeworkAttachmentButtonClick = this.onHomeworkAttachmentButtonClick.bind(this);
    this.onHomeworkAttachmentChange = this.onHomeworkAttachmentChange.bind(this);
    this.onHomeworkAttachmentDeleteButtonClick = this.onHomeworkAttachmentDeleteButtonClick.bind(this);
    this.onConfirmationDialogClose = this.onConfirmationDialogClose.bind(this);
    this.onConfirmationDialogConfirm = this.onConfirmationDialogConfirm.bind(this);
  }

  onHomeworkCompletedChange(event, value) {
    const {
      enqueueSnackbar,
      homework,
      reloadHomeworkData,
      selectedPupil,
    } = this.props;

    if (
      !selectedPupil ||
      !homework.status ||
      homework.status.ticked === value
    ) {
      return;
    }

    axios
      .get(`${window.apiUrl}/homeworkticked/${homework.status.id}?pupil_id=${selectedPupil.id}`)
      .then((response) => {
        if (response.data.error) {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        } else {
          reloadHomeworkData();
        }
      })
      .catch((thrown) => {});
  }

  onHomeworkAttachmentButtonClick() {
    this.homeworkAttachmentInput.current.click();
  }

  onHomeworkAttachmentDeleteButtonClick(homeworkAttachmentId, event) {
    event.preventDefault();

    this.setState({
      isConfirmationDialogOpen: true,
      confirmationDialogHomeworkAttachmentId: homeworkAttachmentId,
    });
  }

  onHomeworkAttachmentChange(event) {
    const {
      enqueueSnackbar,
      t,
      homework,
      reloadHomeworkData,
      selectedPupil,
    } = this.props;

    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (file.size > this.maxFileSize) {
      enqueueSnackbar(t('Attachment exceeds 250mb limit'), {
        variant: 'error',
        autoHideDuration: 2000,
      });

      return;
    }

    const data = new FormData();
    data.append('file', file);
    data.append('pupil_id', selectedPupil.id);

    this.setState({
      isHomeworkAttachmentUploading: true,
    });

    axios
      .post(
        `${window.apiUrl}/uploadhomeworkattachment/${homework.status.id}`,
        data, {
        cancelToken: new axios.CancelToken((c) => {
          this.axiosCancel = c
        }),
      }
      )
      .then((response) => {
        if (response.data.error) {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 3000,
          });
        } else {
          enqueueSnackbar(
            t('You have successfully uploaded the attachment.'),
            {
              variant: 'success',
              autoHideDuration: 2000,
            }
          );
        }

        if (
          this.homeworkAttachmentInput &&
          this.homeworkAttachmentInput.current
        ) {
          this.homeworkAttachmentInput.current.value = null;
        }

        this.setState({
          isHomeworkAttachmentUploading: false,
        });

        reloadHomeworkData();
      })
      .catch(() => {
        enqueueSnackbar(
          t('Something went wrong while uploading the file. Please try again.'),
          {
            variant: 'error',
            autoHideDuration: 2000,
          }
        );

        if (
          this.homeworkAttachmentInput &&
          this.homeworkAttachmentInput.current
        ) {
          this.homeworkAttachmentInput.current.value = null;
        }

        this.setState({
          isHomeworkAttachmentUploading: false,
        });
      })
    ;
  }

  onConfirmationDialogClose() {
    this.setState({
      isConfirmationDialogOpen: false,
      confirmationDialogHomeworkAttachmentId: 0,
    });
  }

  onConfirmationDialogConfirm() {
    const {
      enqueueSnackbar,
      t,
      reloadHomeworkData,
    } = this.props;
    const {
      confirmationDialogHomeworkAttachmentId,
    } = this.state;

    axios
      .post(
        `${window.apiUrl}/deletehomeworkattachment/${confirmationDialogHomeworkAttachmentId}`,
        {},
        {
          cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.error) {
          enqueueSnackbar(response.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        } else {
          enqueueSnackbar(t('You have successfully deleted the attachment.'), {
            variant: 'success',
            autoHideDuration: 2000,
          });
        }

        this.setState({
          isConfirmationDialogOpen: false,
          confirmationDialogHomeworkAttachmentId: 0,
        });

        reloadHomeworkData();
      })
      .catch((thrown) => {});
  }

  /**
   * TODO convert this to component
   * @param attachment
   * @param event
   */
  onDownloadDialogOpen(attachment, event) {
    if (attachment.scan_status === 'pass') {
      return true;
    }

    event.preventDefault();

    FileAttachmentDownloadDialog(null, {
      scan_status: attachment.scan_status,
      scan_status_message: attachment.scan_status_message
    });
  }

  render() {
    const {
      t,
      classes,
      homework,
      homeworksMeta,
    } = this.props;
    const {
      isConfirmationDialogOpen,
      isHomeworkAttachmentUploading,
    } = this.state;

    const isHomeworkAttachmentSelected = this.homeworkAttachmentInput &&
      this.homeworkAttachmentInput.current &&
      this.homeworkAttachmentInput.current.value;

    if (homework.hasOwnProperty('description')) {
      let html = homework.description;
      html = html.replace(/target="[a-z_]+"/g, '');
      if (typeof isIosApp !== 'undefined' && isIosApp) {
        html = html.replace('<a href', '<a target="_system" href');
      } else {
        html = html.replace('<a href', '<a target="_blank" href');
      }

      homework.description = html;
    }

    const target = typeof isIosApp !== 'undefined' && isIosApp ? '_system' : '_blank';
    const isCompleted = homework &&
      homework.status &&
      homework.status.ticked
      ? homework.status.ticked === 'yes'
      : false;

    return (
      <div className="homework-details">
        <div className={classes.content}>
          <div style={{ marginBottom: 20 }}>
            <HomeworkGroupHeaderBadge status={getHomeworkStatus(homework)} />
          </div>
          <Typography
            variant="h4"
            component="h4"
            className={classes.contentHeading}
          >
            {homework.title}
          </Typography>
          <Typography
            variant="h5"
            component="h5"
            className={classes.contentSubHeading}
          >
            {homework.lesson && (
              <span>
                {homework.subject && (
                  <span>{homework.subject} - </span>
                )}
                <span>{homework.lesson} - </span>
              </span>
            )}
            {homework.teacher && (
              <span>{homework.teacher}</span>
            )}
          </Typography>
          <div className={classes.contentMeta}>
            {homework.homework_type && (
              <div>
                <b>{t('Type')}: </b>
                {homework.homework_type}
              </div>
            )}
            {homework.issue_date && (
              <div>
                <b>{t('Issue date')}: </b>
                <Moment date={homework.issue_date} format={'dddd ' + dateFormat} />
              </div>
            )}
            {homework.due_date && (
              <div>
                <b>{t('Due date')}: </b>
                <Moment date={homework.due_date} format={'dddd ' + dateFormat} />
              </div>
            )}
            {homework.status?.mark && (
              <div>
                <b>{t('Mark')}: </b>
                <span>{homework.status.mark}</span>
              </div>
            )}
            {homework.completion_time_value && (
              <div>
                <b>{t('Estimated completion time')}: </b>
                <span>{homework.completion_time_value} {homework.completion_time_unit}</span>
              </div>
            )}
          </div>
          {homework?.status?.allow_marking_completed && (
            <div className={classes.dialogHomeworkCompletedWrapper}>
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCompleted}
                      onChange={this.onHomeworkCompletedChange}
                      color="primary"
                    />
                  }
                  label={t('Completed?')}
                />
              </FormControl>
            </div>
          )}
          <div className={classes.paddingVertical}>
            <div dangerouslySetInnerHTML={{ __html: homework.description }} />
          </div>
          {homework.validated_attachments && homework.validated_attachments.length > 0 && (
            <div className={classes.contentSection}>
              <Typography
                className={classes.contentTitle}
                variant="h6"
                component="h6"
              >
                {t('Attachments')}
              </Typography>
              <List className={classes.breakWord}>
                {homework.validated_attachments.map((homeworkAttachment) => {
                  const primary = (
                    <FileAttachmentRow attachment={homeworkAttachment} />
                  );

                  let link = homeworkAttachment.validated_file;
                  if (link && typeof isIosApp !== 'undefined' && isIosApp) {
                    const sep = link.match(/\?/) ? '&' : '?';
                    link = link + sep + 'delayed=yes';
                  }

                  return (
                    <ListItem
                      button
                      component="a"
                      key={homeworkAttachment.id}
                      href={link}
                      target={target}
                      onClick={this.onDownloadDialogOpen.bind(this, homeworkAttachment)}
                    >
                      <ListItemText primary={primary} />
                    </ListItem>
                  );
                })}
              </List>
            </div>
          )}
          {homework.validated_links && homework.validated_links.length > 0 && (
            <div className={classes.contentSection}>
              <Typography
                className={classes.contentTitle}
                variant="h6"
                component="h6"
              >
                {t('Links')}
              </Typography>
              <List className={classes.breakWord}>
                {homework.validated_links.map((homeworkLink) => {
                  const primary = (
                    <Fragment>
                      <OpenInNew className={classes.buttonIcon} />
                      <span className={classes.homeworkLinkListItemText}> {homeworkLink.link}</span>
                    </Fragment>
                  );

                  let link = homeworkLink.validated_link;
                  if (typeof isIosApp !== 'undefined' && isIosApp) {
                    const sep = link.match(/\?/) ? '&' : '?';
                    link = link + sep + 'delayed=yes';
                  }

                  return (
                    <ListItem
                      button
                      key={homeworkLink.link}
                      component="a"
                      href={link}
                      target={target}
                    >
                      <ListItemText primary={primary} />
                    </ListItem>
                  );
                })}
              </List>
            </div>
          )}
          {homework && homework.status && homework.status.attachments.length > 0 && (
            <div className={classes.contentSection}>
              <Typography
                className={classes.contentTitle}
                variant="h6"
                component="h6"
              >
                {t('My attachments')}
              </Typography>
              <List className={classes.breakWord} component="div">
                {homework && homework.status && homework.status.attachments.map((homeworkAttachment) => {
                  let link = homeworkAttachment.validated_file;
                  if (link && typeof isIosApp !== 'undefined' && isIosApp) {
                    const sep = link.match(/\?/) ? '&' : '?';
                    link = link + sep + 'delayed=yes';
                  }

                  const teacherAttachments = homeworkAttachment.teacher_homework_attachments
                    ? homeworkAttachment.teacher_homework_attachments
                    : [];

                  let secondaryText = '';
                  if (homeworkAttachment.teacher_note || teacherAttachments.length) {
                    secondaryText = (
                      <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                        <span
                          key={key}
                          className={classes.link}
                          onClick={() => {
                            window.open(decoratedHref, typeof isIosApp !== 'undefined' && isIosApp ? '_system' : '_blank');
                          }}
                        >
                          {decoratedText}
                        </span>
                      )}>
                        {t('Teacher\'s note')}: {homeworkAttachment.teacher_note}
                      </Linkify>
                    );
                  }

                  const primary = (
                    <Fragment>
                      <FileAttachmentRow attachment={homeworkAttachment} />
                      {homeworkAttachment.can_delete && (
                        <DeleteIcon
                          className={classes.closeButtonIcon}
                          onClick={this.onHomeworkAttachmentDeleteButtonClick.bind(this, homeworkAttachment.id)}
                        />
                      )}
                    </Fragment>
                  );

                  return [
                    <ListItem
                      key={homeworkAttachment.id}
                      button
                      component="a"
                      href={link}
                      onClick={this.onDownloadDialogOpen.bind(this, homeworkAttachment)}
                      target={target}
                      style={teacherAttachments.length ? { marginBottom: -16 } : {}}
                    >
                      <ListItemText
                        primary={primary}
                        secondary={secondaryText}
                      />
                    </ListItem>,
                    teacherAttachments.map((teacherHomeworkAttachment) => {
                      let link = teacherHomeworkAttachment.validated_file;
                      if (link && typeof isIosApp !== 'undefined' && isIosApp) {
                        const sep = link.match(/\?/) ? '&' : '?';
                        link = link + sep + 'delayed=yes';
                      }

                      const teacherPrimary = (
                        <FileAttachmentRow attachment={teacherHomeworkAttachment} />
                      );

                      return (
                        <ListItem
                          key={'feedback-' + teacherHomeworkAttachment.id}
                          button
                          component="a"
                          href={link}
                          target={target}
                          onClick={this.onDownloadDialogOpen.bind(this, teacherHomeworkAttachment)}
                          style={{ marginLeft: 32, zoom: 0.8 }}
                        >
                          <ListItemText primary={teacherPrimary} />
                        </ListItem>
                      );
                    })
                  ];
                }).flat()}
              </List>
            </div>
          )}
          {homework?.status?.allow_attachments && (
            <div>
              <Fab
                size="small"
                variant="extended"
                color="secondary"
                onClick={this.onHomeworkAttachmentButtonClick}
                disabled={isHomeworkAttachmentUploading}
              >
                <AddIcon className={classes.uploadAttachmentIcon} />
                {isHomeworkAttachmentSelected && (
                  <span>
                    <span>{t('Uploading')} </span>
                    {this.homeworkAttachmentInput.current.files.item(0).name}
                  </span>
                )}
                {!isHomeworkAttachmentSelected && (
                  <span>{t('Upload attachment')}</span>
                )}
                {isHomeworkAttachmentUploading && (
                  <CircularProgress
                    className={classes.uploadAttachmentLoader}
                    size={20}
                  />
                )}
              </Fab>
              <p>
                {t('A maximum of {{maxFilesAllowed}} attachments can be uploaded, each up to 250mb in size.', {
                  maxFilesAllowed: homeworksMeta.max_files_allowed,
                })}
              </p>
              {homeworksMeta.allowed_file_types && (
                <p>
                  <span>{t('Supported file formats')}: </span>
                  {homeworksMeta.allowed_file_types.join(', ')}
                </p>
              )}
              <input
                ref={this.homeworkAttachmentInput}
                type="file"
                style={{ display: 'none' }}
                onChange={this.onHomeworkAttachmentChange}
              />
            </div>
          )}
        </div>
        <ConfirmDialog
          useSuspense={false}
          title={t('Confirmation')}
          isOpen={isConfirmationDialogOpen}
          onClose={this.onConfirmationDialogClose}
          onConfirm={this.onConfirmationDialogConfirm}
        >
          {t('Are you sure you want to remove this attachment?')}
        </ConfirmDialog>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withSnackbar(withTranslation()(HomeworkDetails)))
);
